import { Maybe } from 'graphql/jsutils/Maybe';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { EntityGstStatus, EntityInEstateStatus } from '@/types/schema';

import {
  EntityGstStatusDisplayNames,
  EntityInEstateStatusDisplayNames,
} from '../details/entityDetailPageUtils';

export interface GetAssetLocationDisplayInput {
  inEstateStatus: Maybe<EntityInEstateStatus>;
  /**
   * Entity-level state in-estate status. This is the source of truth for an
   * entity's state estate status.
   */
  stateInEstateStatus?: Maybe<EntityInEstateStatus>;
  gstStatus: Maybe<EntityGstStatus>;
}

export interface GetAssetLocationDisplayOutput {
  locationDisplay: string | null;
  /**
   * Populated only when the entity has an explicit state-level in-estate
   * status that matches its federal status. Holds `locationDisplay` with the
   * redundant "(Federal & State)" qualifier removed (any GST suffix is
   * preserved). Consumers that want the simplified label — e.g. the entity
   * cover slide — should prefer this over `locationDisplay` when present.
   */
  locationDisplaySimplified?: string;
  /**
   * `locationDisplay` without the trailing GST suffix (and, when there is no
   * state-level status, without the "(Federal)" qualifier). Only produced by
   * `getAssetLocationDisplay`.
   */
  locationDisplayShort?: string | null;
  gstStatusDisplay: string | null | undefined;
}

export function getAssetLocationDisplay({
  inEstateStatus,
  stateInEstateStatus,
  gstStatus,
}: GetAssetLocationDisplayInput): GetAssetLocationDisplayOutput {
  if (!inEstateStatus) {
    return {
      locationDisplay: null,
      locationDisplayShort: null,
      gstStatusDisplay: null,
    };
  }

  const estateStatusFederal = EntityInEstateStatusDisplayNames[inEstateStatus];
  const gstStatusDisplay = gstStatus
    ? EntityGstStatusDisplayNames[gstStatus]
    : undefined;

  // When the entity carries an explicit state-level status (asset-level tax
  // situs flag), present a simple "Federal / State" pair instead of per-state
  // breakdowns. The caller has opted into this mode by providing a non-null
  // `stateInEstateStatus`.
  if (stateInEstateStatus) {
    const estateStatusState =
      EntityInEstateStatusDisplayNames[stateInEstateStatus];
    const isSameStatus = inEstateStatus === stateInEstateStatus;
    const locationDisplayShort = isSameStatus
      ? `${estateStatusFederal} (Federal & State)`
      : `${estateStatusFederal} (Federal), ${estateStatusState} (State)`;
    let locationDisplay = locationDisplayShort;
    // When federal and state match, the "(Federal & State)" qualifier is
    // redundant, so offer a simplified variant that drops it.
    let locationDisplaySimplified = isSameStatus
      ? estateStatusFederal
      : undefined;
    if (gstStatusDisplay) {
      locationDisplay += ` — ${gstStatusDisplay}`;
      if (locationDisplaySimplified) {
        locationDisplaySimplified += ` — ${gstStatusDisplay}`;
      }
    }
    return {
      locationDisplay,
      locationDisplaySimplified,
      locationDisplayShort,
      gstStatusDisplay,
    };
  }

  // No state-level status: present federal only.
  let locationDisplay = `${estateStatusFederal} (Federal)`;
  if (gstStatusDisplay) {
    locationDisplay += ` — ${gstStatusDisplay}`;
  }

  return {
    locationDisplay,
    locationDisplayShort: `${estateStatusFederal}`,
    gstStatusDisplay,
  };
}

export function getFederalAssetLocationDisplay({
  inEstateStatus,
  gstStatus,
}: GetAssetLocationDisplayInput): GetAssetLocationDisplayOutput {
  if (!inEstateStatus) {
    return {
      locationDisplay: null,
      gstStatusDisplay: null,
    };
  }

  const locationDisplay =
    EntityInEstateStatusDisplayNames[inEstateStatus] || EMPTY_CONTENT_HYPHEN;
  const gstStatusDisplay = gstStatus
    ? EntityGstStatusDisplayNames[gstStatus]
    : undefined;
  return {
    locationDisplay,
    gstStatusDisplay,
  };
}

export function getStateAssetLocationDisplay({
  stateInEstateStatus,
}: GetAssetLocationDisplayInput): GetAssetLocationDisplayOutput {
  if (stateInEstateStatus) {
    const display = EntityInEstateStatusDisplayNames[stateInEstateStatus];
    return {
      locationDisplay: display,
      gstStatusDisplay: null,
    };
  }

  return {
    locationDisplay: null,
    gstStatusDisplay: null,
  };
}
